// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renders the application, template, and naming choices for workspace creation.
 *
 * These controlled steps expose user intent only; the owning wizard retains
 * role filtering, validation, navigation, and the mutation boundary so visual
 * components cannot create partially configured workspaces.
 */
import type { KeyboardEvent } from 'react';
import {
  AppWindow,
  Check,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Gavel,
  Layers3,
  Plus,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { Input, Label } from '@/components/ui';
import type { ApplicationInfo, TemplateInfo } from '../../services/templates-api';

export type WizardStep = 'application' | 'template' | 'name';
export type TemplateChoice = TemplateInfo | 'blank' | null;

const STEPS: Array<{ id: WizardStep; label: string }> = [
  { id: 'application', label: 'Application' },
  { id: 'template', label: 'Template' },
  { id: 'name', label: 'Name' },
];

const APPLICATION_ICONS: Record<string, LucideIcon> = {
  'contract-review': FileText,
  i9: ClipboardCheck,
  'legislative-review': Gavel,
  srm: ShieldCheck,
};

export function WizardStepper({
  step,
  onJump,
}: {
  step: WizardStep;
  onJump: (step: WizardStep) => void;
}) {
  const currentIndex = STEPS.findIndex(item => item.id === step);

  return (
    <div className="flex items-center gap-2.5 px-6 pb-0.5 pt-4.5" aria-label="Workspace creation progress">
      {STEPS.map((item, index) => {
        const completed = index < currentIndex;
        const current = index === currentIndex;
        return (
          <div key={item.id} className="contents">
            {index > 0 && (
              <div
                className={`h-0.5 flex-1 rounded-full transition-colors duration-[250ms] ease-[ease] ${index <= currentIndex ? 'bg-[#0f172a]' : 'bg-[#e2e8f0]'}`}
                aria-hidden="true"
              />
            )}
            <button
              type="button"
              data-testid={`workspace-step-${item.id}`}
              onClick={() => completed && onJump(item.id)}
              disabled={!completed}
              title={completed ? `Go back to ${item.label}` : undefined}
              className="inline-flex items-center gap-[7px] rounded-lg p-0.5 disabled:cursor-default disabled:opacity-100"
            >
              <span
                className={`inline-flex h-[21px] w-[21px] items-center justify-center rounded-full border-[1.5px] text-[11px] font-[650] transition-colors duration-[250ms] ease-[ease] ${completed || current ? 'border-[#0f172a] bg-[#0f172a] text-white' : 'border-[#cbd5e1] bg-white text-[#94a3b8]'}`}
              >
                {completed ? <Check className="h-[11px] w-[11px]" strokeWidth={3} /> : index + 1}
              </span>
              <span className={`text-[12.5px] font-semibold transition-colors duration-[250ms] ease-[ease] ${completed || current ? 'text-[#0f172a]' : 'text-[#94a3b8]'}`}>
                {item.label}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ChoiceCard({
  title,
  description,
  selected,
  Icon,
  onSelect,
  testId,
}: {
  title: string;
  description: string | null;
  selected: boolean;
  Icon: LucideIcon;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex w-full items-center gap-3.5 rounded-[11px] border bg-white px-4 py-[15px] text-left shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-[border-color,box-shadow,transform] duration-[140ms] ease-[ease] hover:-translate-y-px hover:border-[#94a3b8] hover:shadow-[0_4px_14px_rgba(2,8,23,0.08)] active:translate-y-0 ${selected ? 'border-[#0f172a]' : 'border-[#e8edf3]'}`}
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#eef2f7] text-[#475569]">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-semibold leading-[1.35] text-slate-900">{title}</span>
        {description && <span className="mt-[3px] block text-[13px] leading-[1.45] text-slate-500 [text-wrap:pretty]">{description}</span>}
      </span>
      {selected ? (
        <span className="inline-flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-white">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : (
        <ChevronRight className="h-[18px] w-[18px] shrink-0 text-[#cbd5e1]" />
      )}
    </button>
  );
}

export function ApplicationStep({
  applications,
  selectedApp,
  loading,
  onSelect,
}: {
  applications: ApplicationInfo[];
  selectedApp: ApplicationInfo | null;
  loading: boolean;
  onSelect: (app: ApplicationInfo) => void;
}) {
  if (loading) return <p className="py-8 text-center text-sm text-slate-500">Loading applications...</p>;
  if (applications.length === 0) return <p className="py-8 text-center text-sm text-slate-500">No applications available.</p>;

  return (
    <div className="space-y-2.5">
      {applications.map(app => (
        <ChoiceCard
          key={app.appId}
          title={app.name}
          description={app.description}
          selected={selectedApp?.appId === app.appId}
          Icon={APPLICATION_ICONS[app.appId] ?? AppWindow}
          onSelect={() => onSelect(app)}
          testId={`application-option-${app.appId}`}
        />
      ))}
    </div>
  );
}

export function TemplateStep({
  templates,
  selectedTemplate,
  loading,
  onSelect,
}: {
  templates: TemplateInfo[];
  selectedTemplate: TemplateChoice;
  loading: boolean;
  onSelect: (template: TemplateInfo | 'blank') => void;
}) {
  if (loading) return <p className="py-8 text-center text-sm text-slate-500">Loading templates...</p>;

  return (
    <div className="space-y-2.5">
      <ChoiceCard
        title="Blank Workspace"
        description="Start with an empty workspace"
        selected={selectedTemplate === 'blank'}
        Icon={Plus}
        onSelect={() => onSelect('blank')}
        testId="template-option-blank"
      />
      {templates.map(template => (
        <ChoiceCard
          key={template.templateId}
          title={template.name}
          description={template.description}
          selected={selectedTemplate !== 'blank' && selectedTemplate?.templateId === template.templateId}
          Icon={Layers3}
          onSelect={() => onSelect(template)}
          testId={`template-option-${template.templateId}`}
        />
      ))}
    </div>
  );
}

export function NameStep({
  workspaceName,
  requestPrefix,
  requiresPrefix,
  selectedApp,
  selectedTemplate,
  onWorkspaceName,
  onRequestPrefix,
  onKeyDown,
}: {
  workspaceName: string;
  requestPrefix: string;
  requiresPrefix: boolean;
  selectedApp: ApplicationInfo;
  selectedTemplate: TemplateInfo | 'blank';
  onWorkspaceName: (value: string) => void;
  onRequestPrefix: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const prefixExample = requestPrefix || 'SRM';
  const applicationLabel = selectedApp.name.replace(/ Application$/, '');
  const summary = [
    applicationLabel,
    selectedTemplate === 'blank' ? 'Blank Workspace' : selectedTemplate.name,
    requiresPrefix && requestPrefix ? `Requests start at ${requestPrefix}-1` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="space-y-4">
      <div className="space-y-[7px]">
        <Label htmlFor="workspace-name" className="text-[13px] font-semibold text-[#0f172a]">Workspace name</Label>
        <Input
          id="workspace-name"
          placeholder="e.g. My Contract Review"
          value={workspaceName}
          onChange={event => onWorkspaceName(event.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          className="docreview-wizard-input"
        />
        <p className="text-xs leading-[1.45] text-[#94a3b8]">This will be the display name for your new workspace.</p>
      </div>
      {requiresPrefix && (
        <div className="space-y-[7px]">
          <Label htmlFor="request-prefix" className="text-[13px] font-semibold text-[#0f172a]">
            Request ID prefix <span className="text-red-600">*</span>
          </Label>
          <Input
            id="request-prefix"
            placeholder="e.g. SRM, CR"
            value={requestPrefix}
            onChange={event => onRequestPrefix(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            onKeyDown={onKeyDown}
            maxLength={6}
            className="docreview-wizard-input max-w-[220px] font-mono font-semibold uppercase tracking-[0.06em]"
          />
          <p className="text-xs leading-[1.45] text-[#94a3b8]">
            Uppercase letters and numbers only. New requests will be auto-numbered (e.g. {prefixExample}-1, {prefixExample}-2).
          </p>
        </div>
      )}
      <div data-testid="workspace-summary" className="flex items-center gap-3 rounded-[10px] border border-[#e8edf3] bg-[#fcfdfe] px-4 py-[13px]">
        <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[#eef2f7] text-[#64748b]">
          <AppWindow className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-semibold text-[#0f172a]">{workspaceName || 'Untitled workspace'}</span>
          <span className="block text-xs text-[#64748b]">{summary}</span>
        </span>
      </div>
    </div>
  );
}
